<script setup lang="ts">
  import { ref, watch, onMounted, computed } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { CustomerCase, PatchedCustomerCaseRequest } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import { CasesService } from '@/types/generated/services.gen'
  import type { GenericObject } from 'vee-validate'
  import { Switch } from '@/components/ui/switch'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'

  const props = defineProps<{
    caseData: CustomerCase
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['update:export-legal-insurance-data'])

  const { t } = useTranslation({
    en: {
      legalInsurance: 'Legal Insurance',
      hasLegalInsurance: 'Legal insurance available',
      insuranceCompany: 'Insurance Company',
      policyNumber: 'Policy Number',
      exportLegalInsuranceData: 'Include in export'
    },
    de: {
      legalInsurance: 'Rechtsschutzversicherung',
      hasLegalInsurance: 'Rechtschutzversicherung vorhanden',
      insuranceCompany: 'Versicherungsgesellschaft',
      policyNumber: 'Policennummer',
      exportLegalInsuranceData: 'In Akte anlegen'
    }
  })

  const caseData = ref(props.caseData)
  const hasLegalInsurance = ref(true)
  const exportLegalInsuranceData = ref(false)
  const showLegalInsuranceInfo = ref(true)

  const handleSave = async (updatedData: GenericObject) => {
    if (!hasLegalInsurance.value) {
      return
    }
    const formattedUpdatedData = {
      data: {
        client: {
          ...caseData.value?.data.client,
          ...updatedData,
          legal_insurance: true
        }
      }
    }
    if (caseData.value) {
      try {
        const response = await CasesService.casesPartialUpdate({
          uuid: caseData.value.uuid as string,
          requestBody: formattedUpdatedData as PatchedCustomerCaseRequest
        })
        // Update the caseData ref with the new data
        caseData.value = {
          ...caseData.value,
          data: {
            ...caseData.value.data,
            client: {
              ...caseData.value.data.client,
              ...formattedUpdatedData.data.client
            }
          }
        }
      } catch (e) {
        console.error('Failed to update legal insurance', e)
      }
    }
  }

  watch(hasLegalInsurance, (newValue) => {
    if (!newValue) {
      handleSave({ legal_insurance_name: null, legal_insurance_policy_number: null })
    }
  })

  onMounted(() => {
    exportLegalInsuranceData.value = false
    emit('update:export-legal-insurance-data', false)
  })

  watch(exportLegalInsuranceData, (newValue) => {
    emit('update:export-legal-insurance-data', newValue)
    updateSchema(currentSchema.value)
  })

  const handleSwitchChange = (value: boolean) => {
    exportLegalInsuranceData.value = value
  }

  const baseSchema = {
    legal_insurance_name: yup.string().required('Insurance Company is required'),
    legal_insurance_number: yup.string().optional()
  }

  const currentSchema = computed(() => {
    if (!exportLegalInsuranceData.value) {
      // When exportLegalInsuranceData is false, make all fields optional
      return Object.entries(baseSchema).reduce((acc, [key, value]) => {
        acc[key] = value.clone().notRequired()
        return acc
      }, {})
    }
    return baseSchema
  })

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    showErrors,
    updateSchema
  } = useFormWithValidation(
    {
      legal_insurance_name: caseData.value.data.client.legal_insurance_name || '',
      legal_insurance_number: caseData.value.data.client.legal_insurance_number || ''
    },
    currentSchema.value
  )

  defineExpose({
    validate,
    showErrors
  })
</script>

<template>
  <div
    class="h-auto rounded-xl border bg-white p-6 shadow-md transition-all duration-300 ease-in-out"
  >
    <div
      class="flex items-center justify-between"
      :class="exportLegalInsuranceData ? 'mb-4' : ''"
    >
      <h2 class="text-xl font-semibold text-gray-900">{{ t('legalInsurance') }}</h2>
      <div class="flex items-center space-x-4">
        <div class="flex items-center space-x-2">
          <label
            :for="t('exportLegalInsuranceData')"
            class="text-sm text-gray-700"
          >
            {{ t('exportLegalInsuranceData') }}
          </label>
          <Switch
            :id="t('exportLegalInsuranceData')"
            v-model="exportLegalInsuranceData"
            :checked="exportLegalInsuranceData"
            class="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-gray-200"
            thumb-class=""
            @update:checked="handleSwitchChange"
          />
        </div>
      </div>
    </div>
    <div v-if="exportLegalInsuranceData && showLegalInsuranceInfo">
      <AutoSavingForm
        v-if="hasLegalInsurance"
        :debounce-delay="debounceDelay"
        :throttle-interval="throttleInterval"
        class="space-y-8"
        @save="handleSave"
      >
        <div class="grid grid-cols-1 gap-x-2 gap-y-1 sm:grid-cols-2">
          <div class="sm:col-span-1">
            <InputField
              v-model="caseData.data.client.legal_insurance_name"
              :label="t('insuranceCompany')"
              name="legal_insurance_name"
              :error="formErrors?.value?.legal_insurance_name ?? null"
              :rules="yup.string().required()"
              @update:model-value="(value: any) => handleFieldChange('legal_insurance_name', value)"
              @focus="() => handleFieldTouch('legal_insurance_name')"
            />
          </div>
          <div class="sm:col-span-1">
            <InputField
              v-model="caseData.data.client.legal_insurance_number"
              :label="t('policyNumber')"
              name="legal_insurance_number"
              :rules="yup.string().optional()"
              @update:model-value="
                (value: any) => handleFieldChange('legal_insurance_number', value)
              "
              @focus="() => handleFieldTouch('legal_insurance_number')"
            />
          </div>
        </div>
      </AutoSavingForm>
    </div>
  </div>
</template>
